import { useEffect } from "react"
import { Link, useNavigate, useOutletContext, useParams } from "react-router-dom"
import { useComputed, useSignal } from "@preact/signals-react"

import Alert from 'react-bootstrap/Alert'
import Breadcrumb from 'react-bootstrap/Breadcrumb'
import Button from 'react-bootstrap/Button'
import Col from 'react-bootstrap/Col'
import Dropdown from 'react-bootstrap/Dropdown'
import Form from 'react-bootstrap/Form'
import Row from 'react-bootstrap/Row'
import Nav from 'react-bootstrap/Nav'
import AdministrationTabs from '../components/AdministrationTabs.jsx'
import ConfirmationModal from "../../../components/ConfirmationModal.jsx"
import DebugAccordion from "../../../components/DebugAccordion.jsx"
import Icon from "../../../components/Icon.jsx"
import Loadable from "../../../components/Loadable.jsx"
import NewBusinessEntityModal from './components/NewBusinessEntityModal.jsx'
import PageTitle from "../../../components/PageTitle.jsx"
import ShowMerchantForm from './components/ShowMerchantForm.jsx'
import ShowOrganizationForm from './components/ShowOrganizationForm.jsx'

import { fetchBusinessEntity, deleteMerchant, deleteOrganization, deactivateOrganization, updateMerchant, updateOrganization } from "../../../state/actions/businessEntityActions.js"
import { fetchCurrencies } from '../../../state/actions/currencyActions.js'
import { fetchGroupList, fetchGroupBusinessEntities, fetchGroupRoleBusinessEntities } from '../../../state/actions/groupActions.js'
import { addNotification, NotificationTypes, notifyException } from "../../../state/actions/notificationActions.js"
import { fetchOrganizationRoleList, fetchUserRoleBusinessEntities } from '../../../state/actions/roleActions.js'
import { fetchSystemUserList, fetchUserList } from '../../../state/actions/userActions.js'

import { currentBusinessEntitySignal, businessEntitiesLoadedSignal, businessEntityDictionarySignal, businessEntitiesErrorSignal } from '../../../state/signals/businessEntitySignals'
import { currenciesSignal, currenciesLoadedSignal, currenciesErrorSignal } from '../../../state/signals/currencySignals'
import { groupListSignal, groupListLoadedSignal, groupListErrorSignal, groupBusinessEntitiesSignal, groupBusinessEntitiesLoadedSignal, groupBusinessEntitiesErrorSignal } from '../../../state/signals/groupSignals'
import { organizationRoleListSignal, organizationRoleListLoadedSignal, organizationRoleListErrorSignal, roleBusinessEntitiesSignal, roleBusinessEntitiesLoadedSignal, roleBusinessEntitiesErrorSignal } from '../../../state/signals/roleSignals.js'
import { userListSignal, userListLoadedSignal, systemUserListSignal, systemUserListLoadedSignal } from '../../../state/signals/userSignals'
import { debugSignal } from '../../../state/signals/userSettingsSignals.js'

import { BusinessEntityType, SortDirectionType } from "../../../utils/constants.js"
import { entityProxy } from "../../../utils/debug.js"
import { assembleEntity } from "../../../utils/form.js"
import { abridgedClone, getBusinessEntityTypeName, hasPermission } from "../../../utils/utils.js"

const BusinessEntitiesPage = function () {

	const navigate = useNavigate()
	const { user, config, accessToken, userPermissions, userPermissionsSummary, accordionData: sidebarAccordionData } = useOutletContext()
	const { beId } = useParams()

	const disallowEditSignal = useSignal(false)
	const businessEntityNameSignal = useSignal(businessEntitiesLoadedSignal.value?.name)
	const parentNameSignal = useSignal('')
	//const sortColumnSignal = useSignal(null)
	//const sortDirectionSignal = useSignal(SortDirectionType.ASCENDING)

	useEffect(() => {
		if (businessEntitiesLoadedSignal.value) {
			businessEntityNameSignal.value = currentBusinessEntitySignal.value.name
			parentNameSignal.value = currentBusinessEntitySignal.value.parentId > 0 ? 
						currentBusinessEntitySignal.value.parentBusinessEntity.name :
						currentBusinessEntitySignal.value.name
		}
		fetchCurrencies(config, accessToken, true)
		fetchGroupList(beId, null, config, accessToken)
		fetchUserList(beId, config, accessToken)
		fetchSystemUserList(beId, config, accessToken)
		fetchOrganizationRoleList(beId, config, accessToken)
		fetchGroupBusinessEntities(beId, config, accessToken)
		fetchUserRoleBusinessEntities(beId, config, accessToken)
	}, [beId, config, accessToken, businessEntityNameSignal])

	const showConfirmDeactivateBusinessEntityModalSignal = useSignal(false)
	const showConfirmDeleteBusinessEntityModalSignal = useSignal(false)

	const canEditComputed = useComputed(
		() => {
			return userPermissionsSummary?.value?.canEditOrganizations
		}
	)

	const businessEntityTypeComputed = useComputed(() => {
		if (!currentBusinessEntitySignal.value || !currentBusinessEntitySignal.value.businessEntityTypeId) {
			return BusinessEntityType.Unknown
		}
		//console.log("Business entity type ".concat(currentBusinessEntitySignal.value.businessEntityTypeId))
		//console.log(currentBusinessEntitySignal.value)
		return currentBusinessEntitySignal.value.businessEntityTypeId
	})

	const businessEntityTypeStringComputed = useComputed(
		() => {
			if (!businessEntityTypeComputed || !businessEntityTypeComputed.value) {
				return "Error"
			}
			return getBusinessEntityTypeName(businessEntityTypeComputed.value)
		}
	)

	const showConfirmDeleteBusinessEntityModal = () => {
		showConfirmDeleteBusinessEntityModalSignal.value = true
	}

	const showConfirmDeactivateBusinessEntityModal = () => {
		showConfirmDeactivateBusinessEntityModalSignal.value = true
	}

	const handleCancelDeleteBusinessEntity = () => {
		showConfirmDeleteBusinessEntityModalSignal.value = false
	}

	const handleCancelDeactivateBusinessEntity = () => {
		showConfirmDeactivateBusinessEntityModalSignal.value = false
	}

	const canDeleteComputed = useComputed(
		() => {
			if (!businessEntitiesLoadedSignal.value || !currentBusinessEntitySignal.value) {
				//console.log("Current business entity is undefined")
				return false
			}
			if ('companies' in currentBusinessEntitySignal.value && currentBusinessEntitySignal.value.companies.length) {
				//console.log("Current business entity has child BEs")
				return false
			}
			if (!user) {
				//console.log("Unable to evaluate eleigibility for deletion without current user loaded")
				return false
			}
			if (!('businessEntityId' in user)) {
				//console.log("Can't evaluate user BEID")
				return false
			}
			if (!(user.businessEntityId in businessEntityDictionarySignal.value)) {
				//console.log("Can't evaluate eligibility: User's business entity is not present in the dictionary")
				return false
			}
			let node = businessEntityDictionarySignal.value[user.businessEntityId]
			while (node) {
				if (node.id == beId) {
					//console.log("Can't delete: current business entity is the user's BE or a parent of the user's BE")
					return false
				}
				if ('parentBusinessEntity' in node) {
					node = node.parentBusinessEntity
				}
				else {
					break
				}
			}
			if (currentBusinessEntitySignal.value.isMerchant) {
				//console.log("Can delete: current business entity is a merchant")
				return true
			}
			if (!currentBusinessEntitySignal.value.isRoot) {
				//console.log("Can delete: current business entity is non-root")
				return true
			}
			//console.log("No reason to disqualify deletion")
			return true
		}
	)

	const deleteBusinessEntity = async () => {
		try {
			//console.log("Inside deleteBusinessEntity")
			showConfirmDeleteBusinessEntityModalSignal.value = false
			if (!currentBusinessEntitySignal.value) {
				throw new Error("Business entity signal has no value")
			}
			if (!currentBusinessEntitySignal.value.parentBusinessEntity) {
				throw new Error("Deleted business entity does not know about its parent")
			}
			const type = businessEntityTypeComputed.value
			const typestring = businessEntityTypeStringComputed.value.toLowerCase()
			const name = currentBusinessEntitySignal.value.name
			switch (type) {
				case BusinessEntityType.Merchant:
					await deleteMerchant(beId, config, accessToken)
					break
				case BusinessEntityType.Organization:
					await deleteOrganization(beId, config, accessToken)
					break
				case BusinessEntityType.Reseller:
				default:
					throw new Error(`Cannot delete business entity with invalid type "${businessEntityTypeStringComputed.value}"`)
			}
			//console.log("Got the following response text:")
			//console.log(result)
			//console.log("Business entity signal contains the following:")
			//console.log(currentBusinessEntitySignal.value)
			currentBusinessEntitySignal.value = currentBusinessEntitySignal.value.parentBusinessEntity
			showConfirmDeleteBusinessEntityModalSignal.value = false
			//navigate(`/be/${currentBusinessEntitySignal.value.id}/management/accounts`)
			refetchBusinessEntity()
			addNotification(`Successfully deleted ${typestring} "${name}"`, NotificationTypes.SUCCESS, true)
		} catch (x) {
			addNotification(`Failed delete business entity: Caught exception `.concat(x.toString()), NotificationTypes.ERROR, true)
		}
	}

	const deactivateBusinessEntity = async () => {
		try {
			showConfirmDeactivateBusinessEntityModalSignal.value = false
			if (!currentBusinessEntitySignal.value) {
				throw new Error("Business entity signal has no value")
			}
			const typestring = businessEntityTypeStringComputed.value.toLowerCase()
			const name = currentBusinessEntitySignal.value.name
			const parentBusinessEntity = currentBusinessEntitySignal.value.parentBusinessEntity
			await deactivateOrganization(beId, config, accessToken).then(
				() => {
					try {
						currentBusinessEntitySignal.value = parentBusinessEntity
						navigate(`/be/${parentBusinessEntity.id}/management/accounts`)
						addNotification(`Successfully deactivated ${typestring} "${name}"`, NotificationTypes.SUCCESS, true)
					} catch (x) {
						notifyException(x, "Error handling successful deactivation")
					}
				}
			)
		} catch (x) {
			showConfirmDeleteBusinessEntityModalSignal.value = false
			addNotification(`Failed delete business entity: Caught exception `.concat(x.toString()), NotificationTypes.ERROR, true)
		}
	}

	const refetchBusinessEntity = async () => {
		return await fetchBusinessEntity(
			user.businessEntityId,
			config,
			accessToken,
			{
				currentBusinessEntityId: beId,
				includeChildren: true,
				includeParent: true,
				setType: true,
				excludeMerchants: true,
				setMetadata: true
			}
		)
	}

	/*const unassignRateProfile = function (rateProfileBusinessEntityAssignment) {
		window.alert("Entered unassignRateProfile")
	}*/

	/*const deleteMerchantConfirm = function (businessEntity) {
		window.alert("Entered deleteMerchantConfirm")
	}*/

	/*const showNewOrganizationModalSignal = useSignal(false)
	const showModalNewOrganization = function (businessEntityId) {
		//window.alert("Entered showModalNewOrganization")
		showNewOrganizationModalSignal.value = true
	}*/

	/*const deleteOrganizationConfirm = function (businessEntity) {
		//window.alert("Entered deleteOrganizationConfirm")
	}*/

	const showNewBusinessEntityModalSignal = useSignal(false)
	const newBusinessEntityErrorSignal = useSignal(null)
	const showModalNewBusinessEntity = function () {
		showNewBusinessEntityModalSignal.value = true
	}

	const updateBusinessEntity = async (event) => {
		event.preventDefault()
		let businessEntity = abridgedClone(
			currentBusinessEntitySignal.value,
			[
				"companies",
				"debugInfo",
				"parentBusinessEntity",
				"parentBusinessEntityLoaded",
				"isMerchant",
				"isOrganization",
				"isResellerOrganization",
				"isRootOrganization",
				"isStandardOrganization"
			]
		)
		let changes = assembleEntity(event.target.form)
		Object.keys(changes).forEach(
			(key) => {
				const value = changes[key]
				console.log(`Overriding value of key ${key} with ${value}`)
				businessEntity[key] = value
			}
		)
		businessEntity.parentBusinessEntityId = businessEntity.parentId
		businessEntity.name = businessEntityNameSignal.value
		console.log(`About to rename business entity to ${businessEntity.name}`)
		let update
		switch (currentBusinessEntitySignal.value.businessEntityTypeId) {
			case BusinessEntityType.Organization:
				update = updateOrganization
				break
			case BusinessEntityType.Merchant:
				update = updateMerchant
				break
			default:
				throw new Error(`Invalid business entity type ${currentBusinessEntitySignal.value.type}`)
		}
		try {
			const be = await update(businessEntity, config, accessToken)
			addNotification(`Successfully updated ${businessEntityTypeStringComputed.value} "${be.name}"`, NotificationTypes.SUCCESS, true, "Update successful")
		} catch (error) {
			addNotification(`Failed to update business entity: ${error.toString()}`, NotificationTypes.ERROR, true)
		}
	}

	const hasNameChangedComputed = useComputed(() => {
		if (!businessEntitiesLoadedSignal.value || !currentBusinessEntitySignal.value) return false
		return businessEntityNameSignal.value !== currentBusinessEntitySignal.value.name
	})


	/*const newCompany = function () {
		window.alert("Entered newCompany")
	}*/

	/*const showCompanyList = function (businessEntity) {
		if (!businessEntity.isMerchant && businessEntity.companies.length > 0 || hasPermission(permissionsSignal, 'billingCompanies', businessEntity, 'canEdit')) {
			return true
		}
		return false
	}*/

	/*const hasRateProfile = function(businessEntity){
		return businessEntity && businessEntity.rateProfileBusinessEntityAssignments && 
		typeof businessEntity.rateProfileBusinessEntityAssignments.length == typeof 0 &&
		businessEntity.rateProfileBusinessEntityAssignments.length > 0
	}*/

	/*const showAssignedRateProfiles = function(businessEntity){
		if(
			hasPermission(userPermissions, 'billingRateProfileAssignments', 'canEdit') || 
			hasPermission(userPermissions, 'billingRateProfileAssignments', 'canView') && 
			hasRateProfile(businessEntity)
		){
			return true
		}
		else{
			return false
		}
	}*/

	/*const handleHeaderTableClick = datafield => {
		// if already sorted on this column then toggle sort direction
		if (datafield == sortColumnSignal.value) {
			sortDirectionSignal.value = sortDirectionSignal.value == SortDirectionType.DESCENDING ? SortDirectionType.ASCENDING : SortDirectionType.DESCENDING
			return
		}

		// update sort column - the computed functions will then run and render the sorted data
		sortColumnSignal.value = datafield
	}*/

	/*function TableColumnHeader({ title, sortable, datafield }) {
		// non-sortable columns do not have click handlers
		if (!sortable) {
			return (
				<th>{title}</th>
			)
		}
		// sortable column but isn't currently sorted on this column. Add click handler but no caret indicators
		if (datafield != sortColumnSignal.value) {
			return (
				<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}</th>
			)
		}
		// datafield matches sortColumn signal so we need to add a caret icon. sortDirection signal determines the direction
		if (sortDirectionSignal.value == SortDirectionType.DESCENDING) {
			return (
				<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
					<Icon icon="caret-down" width={16} height={16}></Icon>
				</th>
			)
		}
		// else must be ascending
		return (
			<th className="sortable-table-header" onClick={() => handleHeaderTableClick(datafield)}>{title}
				<Icon icon="caret-up" width={16} height={16}></Icon>
			</th>
		)
	}*/

	/*const CompanyTableRows = function ({ companies }) {
		if (companies) {
			let rows = []
			for (let i = 0; i < companies.length; i++) {
				const company = companies[i]
				rows.push(
					<tr>
						<td><a href={`/management/companies/company/${beId}/${company.id}`}></a></td>
						<td className='col-xs-11'>{company.name}</td>
						<td className='col-xs-1'>
							{
								hasPermission(userPermissions, "billingCompanies", "canEdit") && (
									<Button variant="outline-secondary" onClick={() => { confirmDeleteCompany(company, false) }}>
										<Icon icon="trash" height={16} width={16} style='font-size:20px;color:#d43f3a'></Icon>
									</Button>
								)
							}
						</td>
					</tr>
				)
			}
			return rows
		} else {
			return (<tr><td style="text-align:center" colSpan="2">No companies to display.</td></tr>)
		}
	}*/

	/*const RateProfileBusinessEntityAssignmentTableRows = function ({ rateProfileBusinessEntityAssignments }) {
		if (rateProfileBusinessEntityAssignments) {
			let rows = []
			for (let i = 0; i < rateProfileBusinessEntityAssignments.length; i++) {
				const rateProfileBusinessEntityAssignment = rateProfileBusinessEntityAssignments[i]
				rows.push(
					<tr>
						<td>{rateProfileBusinessEntityAssignment.rateProfile.name}</td>
						<td>{rateProfileBusinessEntityAssignment.rateProfile.billableProduct.name}</td>
						<td className='textRight'>
							{
								hasPermission(userPermissions, 'billingRateProfileAssignments', 'canEdit') && (
									<Button variant="outline-secondary" onClick={() => {unassignRateProfile(rateProfileBusinessEntityAssignment) }}>
										<Icon icon="times" height={16} width={16}></Icon>
									</Button>
								)
							}
						</td>
					</tr>
				)
			}
			return rows
		} else {
			return (<tr><td style="text-align:center" colSpan="3">No rate profiles assigned.</td></tr>)
		}
	}*/

	return (
		<>
			<Breadcrumb className="px-3 pt-1 bg-light">
				<Breadcrumb.Item linkAs={Link} linkProps={{ to: `/be/${beId}/management/accounts` }}>Administration</Breadcrumb.Item>
				<Breadcrumb.Item active>Business Entities</Breadcrumb.Item>
			</Breadcrumb>
			<PageTitle title='Business Entities' canEditSignal={canEditComputed} />
			<AdministrationTabs activeKey="1" />

			<Nav className="justify-content-between px-3 py-1 my-2 bg-light">
				<Loadable loadedSignals={[businessEntitiesLoadedSignal]} errorSignals={[businessEntitiesErrorSignal]}>
					<p className="me-auto">{businessEntitiesLoadedSignal.value ? currentBusinessEntitySignal.value?.name : "Loading..."}</p>
				</Loadable>
				{canEditComputed.value &&
					<Nav.Item className="mt-1 me-2">
						<Button size="sm" variant="outline-secondary"
							className="d-flex align-items-center justify-content-center"
							onClick={() => { showModalNewBusinessEntity(currentBusinessEntitySignal.value) }}>
							<Icon icon='plus-circle' width="16" height="16" ></Icon>&nbsp;New Child Account</Button>
					</Nav.Item>}
				{canDeleteComputed.value && currentBusinessEntitySignal.value.isOrganization &&
					<Nav.Item className="mt-1">
						<Button size="sm" variant="outline-secondary"
							className="d-flex align-items-center justify-content-center"
							onClick={showConfirmDeactivateBusinessEntityModal}>
							<Icon icon='toggle-on' width="16" height="16" ></Icon>&nbsp;Deactivate</Button>
					</Nav.Item>}
			</Nav>

			{newBusinessEntityErrorSignal.value && (
				<Alert variant="danger" onClose={() => newBusinessEntityErrorSignal.value = null} dismissible className="mx-3 mt-2">
					{newBusinessEntityErrorSignal.value}
				</Alert>
			)}

			{/* <header className="panel-heading breakWordWrap">
				<h2 className='panel-title float-start'>
					<Loadable loadedSignals={[businessEntitiesLoadedSignal]} errorSignals={[businessEntitiesErrorSignal]}>
						{businessEntitiesLoadedSignal.value ? currentBusinessEntitySignal.value?.name : "Loading..."}
					</Loadable>
				</h2>
				{
					(businessEntitiesLoadedSignal.value && userPermissionsSummary.value.canEditOrganizations && (
						currentBusinessEntitySignal.value.isMerchant ||
						currentBusinessEntitySignal.value.isOrganization ||
						!currentBusinessEntitySignal.value.isRoot
					)) ? (
						<span className="float-end">
							{
									currentBusinessEntitySignal.value.isOrganization && (
										<span className="me-2">
											<Button onClick={
												() => {
													showModalNewBusinessEntity(currentBusinessEntitySignal.value)
												}
											}>
												<Icon icon="plus" height={16} width={16}></Icon>&nbsp;Add Child Account
											</Button>
										</span>
									)
								}
								{
									(canDeleteComputed.value) ? (
										currentBusinessEntitySignal.value.isOrganization ? (
											<Button onClick={showConfirmDeactivateBusinessEntityModal}>
												<Icon icon="toggle-on" height={16} width={16}></Icon>&nbsp;Deactivate
											</Button>
										) : (
											<Button onClick={showConfirmDeleteBusinessEntityModal}>
												<Icon icon="trash-o" height={16} width={16}></Icon>&nbsp;Delete
											</Button>
										)
									) : (
										<span style={{ cursor: "not-allowed" }} >
											<Icon icon="no-trash" height={16} width={16} />&nbsp;Can&apos;t Delete
										</span>
									)
								}
						</span>
					) : (<></>)
				}
			</header> */}
			<div className="panel-body">
				<Loadable loadedSignals={[businessEntitiesLoadedSignal, currentBusinessEntitySignal]} errorSignals={[businessEntitiesErrorSignal]} detail={true}>
	
					<Form id="updateBusinessEntityForm" className="newvalidation" spellCheck="false" autoComplete="off" autoCapitalize="off" autoCorrect="off">
						<Row className="mb-3"><Col md={6}>
							<Form.Group>
								<Form.Label>Parent</Form.Label>
								<Form.Control type="text" value={parentNameSignal.value} disabled /></Form.Group>
						</Col></Row>
						<Row className="mb-3"><Col md={6}>
							<Form.Group>
								<Form.Label>Type</Form.Label>
								<Form.Control type="text" value={currentBusinessEntitySignal.value?.BusinessEntityFriendlyTypeName} disabled />
								{/* <input className="form-control" value="Merchant" disabled />
								<input className="form-control" value="Organization" disabled />
								<input className="form-control" value="Reseller" disabled /> */}
							</Form.Group>
							{
								currentBusinessEntitySignal.value && currentBusinessEntitySignal?.value.isMerchant && (
									<ShowMerchantForm merchantSignal={currentBusinessEntitySignal}></ShowMerchantForm>
								)
							}
							{
								currentBusinessEntitySignal.value && currentBusinessEntitySignal.value.isOrganization && (
									<ShowOrganizationForm
										organizationSignal={currentBusinessEntitySignal}
										disallowEditSignal={disallowEditSignal}
										organizationNameSignal={businessEntityNameSignal}
									></ShowOrganizationForm>
								)
							}
						</Col></Row>
						<Row className="mb-3"><Col md={6}>
							<Form.Group>
								{
									hasPermission(userPermissions, "Organizations", "canEdit") && (
										<Button type="submit" disabled={!hasNameChangedComputed.value} onClick={updateBusinessEntity}>Update</Button>
									)
								}
							</Form.Group>
						</Col></Row>
					</Form>
					{/*
					showCompanyList(currentBusinessEntitySignal.value) && (
						<div className="panel panel-transparent col-xs-12">
							<header className='panel-heading panelBorder'>
								{
									hasPermission(permissionsSignal, 'billingCompanies', beId, 'canEdit') && (
										<div className="panel-actions">
											<Button onClick={newCompany}>
												<Icon icon="plus" height={16} width={16}></Icon>
											</Button>
										</div>
									)
								}
								<h2 className="panel-title">Billing Companies</h2>
							</header>
							<div className='panel-body'>
								<Table className="table table-striped table-hover">
									{
										currentBusinessEntitySignal.value.companies && (
											<>
												<thead>
													<tr>
														<TableColumnHeader title={'Name'} sortable={true} datafield={'name'}></TableColumnHeader>
														<th className='col-xs-1'></th>
													</tr>
												</thead>
												<tbody>
													<CompanyTableRows companies={currentBusinessEntitySignal.value.companies}></CompanyTableRows>
												</tbody>
											</>
										)
									}
								</Table>
							</div>
						</div>
					)
				*/}
					{/*
					showAssignedRateProfiles(currentBusinessEntitySignal.value) && (
						<div className="panel panel-transparent col-xs-12">
							<header className='panel-heading panelBorder'>
								{
									hasPermission(permissionsSignal, 'billingRateProfileAssignments', beId, 'canEdit') && (
										<div className='panel-actions'>
											<a href={`/management/organizations/organization/assignRateProfiles`}>
												<Icon icon="plus" height={16} width={16}></Icon>
											</a>
										</div>
									)
								}
								<h2 className='panel-title'>Assigned Rate Profiles</h2>
							</header>
							<div className='panel-body'>
								<Table className="table table-striped">
									{
										currentBusinessEntitySignal.value.rateProfileBusinessEntityAssignments && (
											<>
												<thead>
													<tr>
														<TableColumnHeader title={'Name'} sortable={true} datafield={'name'}></TableColumnHeader>
														<TableColumnHeader title={'Product'} sortable={true} datafield={'billableProduct'} colSpan={2}></TableColumnHeader>
													</tr>
												</thead>
												<tbody>
													<RateProfileBusinessEntityAssignmentTableRows rateProfileBusinessEntityAssignments={currentBusinessEntitySignal.value.rateProfileBusinessEntityAssignments}
													></RateProfileBusinessEntityAssignmentTableRows>
												</tbody>
											</>
										)
									}
								</Table>
							</div>
						</div>
					)
				*/}
				</Loadable>
			</div>

			<NewBusinessEntityModal
				showSignal={showNewBusinessEntityModalSignal}
				parentBusinessEntity={currentBusinessEntitySignal.value}
				currencies={currenciesSignal.value}
				refetchBusinessEntity={refetchBusinessEntity}
				errorSignal={newBusinessEntityErrorSignal}
			></NewBusinessEntityModal>

			<ConfirmationModal
				show={showConfirmDeleteBusinessEntityModalSignal.value}
				handleCancel={handleCancelDeleteBusinessEntity}
				handleConfirm={deleteBusinessEntity}
				message={"Are you sure you want to permanently delete this ".concat(businessEntityTypeStringComputed.value.toLowerCase()).concat("?")}
				title="Confirm Deletion"
				confirmText='Yes'
				cancelText='No'
			></ConfirmationModal>

			<ConfirmationModal
				show={showConfirmDeactivateBusinessEntityModalSignal.value}
				handleCancel={handleCancelDeactivateBusinessEntity}
				handleConfirm={deactivateBusinessEntity}
				message={`Deactivate this organization?`}
				title="Confirm Deactivation"
				confirmText='Yes'
				cancelText='No'
			></ConfirmationModal>

			<DebugAccordion data={{ name: 'Business entity', data: currentBusinessEntitySignal.value }}></DebugAccordion>

			{
				debugSignal.value && (
					<div>
						<div>Business entities loaded: {businessEntitiesLoadedSignal.value ? "true" : "false"}</div>
						<div>Current business entity ID: {!businessEntitiesLoadedSignal.value ? ("Loading") : (currentBusinessEntitySignal.value?.id)}</div>
						<div>Can edit organization: {userPermissionsSummary.value?.canEditOrganizations ? "true" : "false"}</div>
						<div>Business entity is merchant: {currentBusinessEntitySignal.value?.isMerchant ? "true" : "false"}</div>
						<div>Business entity is organization: {currentBusinessEntitySignal.value?.isOrganization ? "true" : "false"}</div>
						<div>Business entity is root: {currentBusinessEntitySignal.value?.isRoot ? "true" : "false"}</div>
						<div>Has groups: {!groupListLoadedSignal.value ? "Loading..." : (groupListSignal.value?.length > 0 ? "true" : "false")}</div>
						<div>Has users: {!userListLoadedSignal.value ? "Loading..." : (userListSignal.value?.length > 0 ? "true" : "false")}</div>
						<div>Has system users: {!systemUserListLoadedSignal.value ? "Loading..." : (systemUserListSignal.value?.length > 0 ? "true" : "false")}</div>
						<div>Has child business entities: {!businessEntitiesLoadedSignal.value ? "Loading..." : (currentBusinessEntitySignal.value?.companies?.length > 0 ? "true" : "false")}</div>
						<div>Has roles: {!organizationRoleListLoadedSignal.value ? "Loading..." : (organizationRoleListLoadedSignal.value?.length > 0 ? "true" : "false")}</div>
					</div>
				)
			}
		</>
	)
}
export default BusinessEntitiesPage